import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import * as authApi from '@/api/auth';
import { tokenStore, bindRefreshHook } from '@/api/http';
import type { AuthIdentity, IssuedTokens, RoleCode } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  const tokens = ref<IssuedTokens | null>(tokenStore.get());
  const identity = ref<AuthIdentity | null>(null);
  const initialized = ref(false);

  const isAuthenticated = computed(() => !!tokens.value?.accessToken && !!identity.value);
  const roles = computed<RoleCode[]>(() => identity.value?.roles ?? []);
  const storeId = computed(() => identity.value?.storeId ?? null);
  const displayName = computed(() => identity.value?.displayName ?? '');
  const storeName = computed(() => identity.value?.storeName ?? '');

  function setTokens(next: IssuedTokens | null): void {
    tokens.value = next;
    if (next) tokenStore.set(next);
    else tokenStore.clear();
  }

  function setIdentity(next: AuthIdentity | null): void {
    identity.value = next;
  }

  async function login(loginName: string, password: string): Promise<void> {
    const issued = await authApi.login({ loginName, password });
    setTokens(issued);
    try {
      await fetchProfile();
    } catch (err) {
      setTokens(null);
      throw err;
    }
    if (!identity.value) {
      throw new Error('门店端仅限门店账号登录，当前账号无法使用');
    }
    if (!identity.value.storeId) {
      setTokens(null);
      setIdentity(null);
      throw new Error('该账号未绑定所属门店，请联系总部在「账号管理」中设置后再登录');
    }
  }

  async function fetchProfile(): Promise<void> {
    if (!tokens.value?.accessToken) {
      identity.value = null;
      return;
    }
    try {
      const me = await authApi.fetchMe();
      if (me.accountKind !== 'STORE') {
        identity.value = null;
        setTokens(null);
        return;
      }
      identity.value = me;
    } catch {
      identity.value = null;
      setTokens(null);
    }
  }

  async function logout(): Promise<void> {
    if (tokens.value?.accessToken) {
      try {
        await authApi.logout(tokens.value.accessToken);
      } catch {
        // ignore network errors during logout
      }
    }
    setTokens(null);
    setIdentity(null);
  }

  async function bootstrap(): Promise<void> {
    if (initialized.value) return;
    initialized.value = true;
    bindRefreshHook({
      refresh: (rt) => authApi.refresh(rt),
      onUnauthorized: () => {
        setTokens(null);
        setIdentity(null);
      },
    });
    if (tokens.value?.accessToken) {
      await fetchProfile();
    }
  }

  return {
    tokens,
    identity,
    isAuthenticated,
    roles,
    storeId,
    displayName,
    storeName,
    login,
    logout,
    fetchProfile,
    bootstrap,
    setTokens,
    setIdentity,
  };
});
